import { StandaloneAppErrorData } from '@dadohr/shared/domainTypes/internalAlerts';
import styled from '@emotion/styled';
import { CssBaseline } from '@material-ui/core';
import React, { useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter as Router, Redirect, Route } from 'react-router-dom';
import { GoogleAnalyticsListener } from '../components/GoogleAnalyticsLIstener';
import { NotFound } from '../components/NotFound';
import { SomethingWentWrong } from '../components/SomethingWentWrong';
import { BackgroundJobsProvider } from '../hooks/useBackgroundJobs';
import { SnackbarProviderConfigured } from '../hooks/useSnackbar';
import { captureException } from '../sentry';
import { getStringFromUnkownError } from '../services/errors';
import { AppThemeProvider } from '../themes/provider';
import { ForceRerenderContext } from './features/Experience/services/forceRerender';
import {
  trackEmployeeAppEvent,
  trackEmployeeAppEventV2
} from './features/Experience/services/productAnalytics';
import {
  CURRENT_DATE_FN,
  StandaloneAppContextProvider,
  StandaloneAppRouting
} from './hooks/useStandaloneAppContext';
import { ROUTES } from './routes';
import { sendEmployeeAppErrorAlert } from './services/alerts';
import { IPage, PAGES } from './sitemap';

// A stale code-split chunk after a deploy fails to import (404 on the old hash).
// The standalone entry auto-reloads on the first vite:preloadError; these aren't
// actionable code errors, so don't fire the employee-app alert for them. new-main.
const isChunkLoadError = (err: unknown): boolean => {
  const msg = err instanceof Error ? `${err.name} ${err.message}` : String(err);
  return /failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module|chunkloaderror/i.test(
    msg
  );
};

const Root = styled('div')`
  display: flex;
  min-height: 100vh;

  strong,
  b {
    font-weight: 400;
  }
`;

const AppContent = styled('div')<{ v2: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: ${(p) =>
    p.v2
      ? p.theme.custom.colors.gray.w100
      : p.theme.custom.colors.background.main};
  max-width: 100%;
`;

const mapPagesToRoutes = (page: IPage, v: number) => {
  return (
    <React.Fragment key={page.path}>
      {page.redirectFrom &&
        page.redirectFrom.map((p) => (
          <Route
            path={p}
            key={p}
            exact
            render={() => <Redirect to={page.path} />}
          />
        ))}
      <Route
        key={page.path}
        path={page.path}
        exact
        render={(props) => (
          <ErrorBoundary
            fallbackRender={() => <SomethingWentWrong height={500} />}
            onError={(err, info) => {
              // Stale lazy-chunk after a deploy (the entry auto-reloads on
              // vite:preloadError) — not an actionable code error; skip Sentry + the alert.
              if (isChunkLoadError(err)) return;
              captureException(err, {
                tags: { errorBoundary: 'standalone-app' },
                extra: { componentStack: info?.componentStack }
              });
              const url = window?.location?.href || '';
              const data: StandaloneAppErrorData = {
                url,
                error: getStringFromUnkownError(err),
                info: getStringFromUnkownError(info),
                userAgent: window.navigator.userAgent,
                accessToken: props.match.params.accessToken || '' // This is based on convention for now, as all routes contain this. Will need to be done differently later
              };
              sendEmployeeAppErrorAlert(data);
            }}
          >
            {page.render(props, v)}
          </ErrorBoundary>
        )}
      />
    </React.Fragment>
  );
};

const MainApp = () => {
  const [v, setV] = useState(0);
  const forceRerender = useMemo(() => {
    return {
      forceRerender: () => setV((x) => (x > 999 ? 0 : x + 1))
    };
  }, []);

  const isV2 = window?.location?.pathname?.startsWith('/v2');

  return (
    <Root>
      <CssBaseline />

      <ForceRerenderContext.Provider value={forceRerender}>
        <AppContent v2={isV2}>
          <Route path="/" exact render={() => <NotFound />} />
          {PAGES.map((p) => mapPagesToRoutes(p, v))}
        </AppContent>
      </ForceRerenderContext.Provider>
    </Root>
  );
};

const ROUTING: StandaloneAppRouting = {
  toMoment: {
    href: (accessToken) => ROUTES.legacy.momentDetails.url(accessToken),
    onClick: () => undefined
  },
  toTask: {
    href: (accessToken, taskId) =>
      ROUTES.legacy.taskDetails.url(accessToken, taskId),
    onClick: () => undefined
  },
  toOverview: {
    href: (accessToken) => ROUTES.legacy.overview.url(accessToken),
    onClick: () => undefined
  }
};

export const StandaloneApp = () => {
  return (
    <AppThemeProvider>
      <StandaloneAppContextProvider
        mode="LIVE"
        timeFn={CURRENT_DATE_FN}
        routing={ROUTING}
        trackProductAnalyticsEvent={trackEmployeeAppEvent}
        trackProductAnalyticsEventV2={trackEmployeeAppEventV2}
      >
        <SnackbarProviderConfigured>
          <BackgroundJobsProvider>
            <Router>
              <GoogleAnalyticsListener app="standalone-app" />
              <MainApp />
            </Router>
          </BackgroundJobsProvider>
        </SnackbarProviderConfigured>
      </StandaloneAppContextProvider>
    </AppThemeProvider>
  );
};
